import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import {
  Badge, Button, Chip, Empty, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { kindsOf, sidesOf, vocabOf } from '../../utils/vertical';
import { colors } from '../../theme';
import { currency } from '../../utils/format';

/** Partner's own products and services, with quick stock / visibility toggles. */
const PartnerCataloguePage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const { partner } = useAuth();
  // The trade's own words: menu / items for a restaurant, rooms for a hotel...
  const vocab = vocabOf(partner);
  // Every side the business has: a tour operator with rooms, a hotel with a restaurant...
  const sides = sidesOf(partner);
  const kinds = kindsOf(partner);
  const food = sides.menu;
  // A hotel and nothing else: its "catalogue" is its rooms, kept on the hotel page.
  const hotelOnly = kinds.length === 1 && kinds[0] === 'hotel';
  useEffect(() => { if (hotelOnly) navigate('/partner/hotel', { replace: true }); }, [hotelOnly, navigate]);

  const tabParam = params.get('tab');
  const tab = tabParam === 'services' || (!tabParam && !sides.products && sides.services) ? 'services' : 'products';
  const [items, setItems] = useState([]);
  // Nothing in the master list for this trade? Then there is nothing to offer.
  const [master, setMaster] = useState({});
  useEffect(() => { api.menu.availability().then((r) => setMaster(r.data || {})).catch(() => setMaster({})); }, []);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const fetcher = tab === 'products' ? api.products.mine : api.services.mine;
    fetcher({ limit: 60 })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const toggleActive = async (item) => {
    const fetcher = tab === 'products' ? api.products.update : api.services.update;
    await fetcher(item._id, { isActive: !item.isActive });
    load();
  };

  const toggleStock = async (item) => {
    await api.products.setStock(item._id, { stock: item.stock > 0 ? 0 : 10 });
    load();
  };

  const remove = async (item) => {
    const ok = await toast.confirm({
      title: 'Delete',
      message: `Remove "${item.title}"?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const fetcher = tab === 'products' ? api.products.remove : api.services.remove;
    await fetcher(item._id);
    load();
  };

  const singular = tab === 'products' ? 'product' : 'service';
  const noun = food && tab === 'products' ? 'item' : singular;

  return (
    <div>
      <PageHeader
        title={kinds.length > 1 ? 'My catalogue' : (vocab.catalogue || 'My catalogue')}
        back={false}
        subtitle={`${items.length} ${food && tab === 'products' ? 'items' : tab}`}
        right={(
          <div className="row" style={{ gap: 8 }}>
            <Button title="My categories" variant="light" size="sm" onClick={() => navigate('/partner/categories')} />
            {(tab === 'services' ? master.service : (food ? master.dish : master.product)) ? (
              <Button
                title={food && tab === 'products' ? 'Pick from master menu' : 'Pick from master list'}
                variant="light"
                size="sm"
                onClick={() => navigate(`/partner/master-menu?type=${tab === 'services' ? 'service' : (food ? 'dish' : 'product')}`)}
              />
            ) : null}
            <Button
              title={`+ Add ${noun}`}
              size="sm"
              onClick={() => navigate(`/partner/catalogue/new?kind=${singular}`)}
            />
          </div>
        )}
      />

      <div className="row" style={{ gap: 8, marginBottom: 16 }}>
        {sides.products || !sides.services ? <Chip label={food ? '🍽️ Menu' : '📦 Products'} active={tab === 'products'} onClick={() => setParams({ tab: 'products' })} /> : null}
        {sides.services || !sides.products ? (
          <Chip label={kinds.includes('travel') ? '🧳 Packages & services' : '🛠️ Services'} active={tab === 'services'} onClick={() => setParams({ tab: 'services' })} />
        ) : null}
        {sides.rooms ? <Chip label="🛏️ Rooms" onClick={() => navigate('/partner/hotel')} /> : null}
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty
          emoji={tab === 'products' ? '📦' : '🛠️'}
          title={`No ${tab} yet`}
          text={`Add your first ${singular} so customers can find it.`}
          actionLabel={`Add ${singular}`}
          onAction={() => navigate(`/partner/catalogue/new?kind=${singular}`)}
        />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 40)} className="card sh-sm">
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                {item.images && item.images[0] ? (
                  <img
                    src={fileUrl(item.images[0])}
                    alt=""
                    style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover', background: colors.bg }}
                  />
                ) : (
                  <span
                    style={{
                      width: 64, height: 64, borderRadius: 12, background: colors.primaryLight,
                      display: 'grid', placeItems: 'center', fontSize: 22, flex: '0 0 auto',
                    }}
                  >
                    {(item.category && item.category.icon) || (tab === 'products' ? '📦' : '🛠️')}
                  </span>
                )}
                <div className="grow">
                  <div className="t-title clamp-2">{item.title}</div>
                  <div className="t-tiny">{item.category && item.category.name}</div>
                  <div className="row" style={{ gap: 8, alignItems: 'baseline', marginTop: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{currency(item.price)}</span>
                    <span className="t-tiny">{tab === 'products' ? item.unit : item.priceUnit}</span>
                  </div>
                  {tab === 'products' ? (
                    <div style={{ marginTop: 6 }}>
                      <Badge
                        value={item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}
                        color={item.stock > 0 ? colors.success : colors.danger}
                      />
                    </div>
                  ) : (
                    <div className="t-tiny" style={{ marginTop: 5 }}>
                      ⏱ {item.durationMinutes} min · {item.bookingCount || 0} booked
                    </div>
                  )}
                </div>
              </div>

              <div
                className="row wrap"
                style={{ gap: 14, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.line}` }}
              >
                <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(item.isActive)}
                    onChange={() => toggleActive(item)}
                    style={{ width: 16, height: 16, accentColor: colors.primary }}
                  />
                  <span className="t-small">Visible to customers</span>
                </label>
                <span className="grow" />
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate(
                    `/partner/catalogue/new?kind=${singular}`,
                    { state: { item } },
                  )}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate(`/${singular}/${item._id}?preview=1`)}
                >
                  👀 View as customer
                </button>
                {tab === 'products' ? (
                  <button type="button" className="link-btn" onClick={() => toggleStock(item)}>
                    {item.stock > 0 ? 'Out of stock' : 'Restock'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="link-btn"
                  style={{ color: colors.danger }}
                  onClick={() => remove(item)}
                >
                  Delete
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerCataloguePage;
